import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { getAccess } from '@/client'
import {
  cancelRequest,
  catalogue,
  listBundles,
  myAccess,
  myRequests,
  requestAccess,
  requestablePlaces,
  roleOffers,
  type AccessKind,
  type NewAccessRequest,
} from '@/lib/auth/accessRequests'

/**
 * What this principal may do — `GET /v1/access`.
 *
 * One query for the whole session: every page that gates a control or scopes
 * a filter reads the same cache entry, so a dashboard with six panels does
 * not ask six times. Five minutes stale: a grant changed in the admin portal
 * shows up at the next page load, which is as fast as the token behind it
 * changes anyway.
 */
export const accessKeys = {
  all: ['access'] as const,
  me: () => [...accessKeys.all, 'me'] as const,
  catalogue: () => [...accessKeys.all, 'catalogue'] as const,
  roles: () => [...accessKeys.all, 'roles'] as const,
  mine: () => [...accessKeys.all, 'mine'] as const,
  requests: () => [...accessKeys.all, 'requests'] as const,
  places: (kind: AccessKind, search: string) =>
    [...accessKeys.all, 'places', kind, search] as const,
  offers: (kind: AccessKind, scopeId: number) =>
    [...accessKeys.all, 'offers', kind, scopeId] as const,
}

/** Characters typed before the place search asks — the service refuses fewer. */
export const MIN_PLACE_SEARCH = 2

export function useAccess() {
  return useQuery({
    queryKey: accessKeys.me(),
    queryFn: async () => (await getAccess({ throwOnError: true })).data,
    staleTime: 5 * 60_000,
  })
}

/**
 * VentaWiz's permissions, for the Request access dialog. Public, and the
 * same for everybody; not retried — the dialog says it cannot offer a list.
 */
export function useAccessCatalogue() {
  return useQuery({
    queryKey: accessKeys.catalogue(),
    queryFn: catalogue,
    staleTime: 5 * 60_000,
    retry: false,
  })
}

/**
 * The organisation's roles, for the same dialog. Often refused to somebody
 * who holds nothing yet; then only permissions are offered.
 */
export function useAccessRoles() {
  return useQuery({
    queryKey: accessKeys.roles(),
    queryFn: listBundles,
    staleTime: 5 * 60_000,
    retry: false,
  })
}

/**
 * Ask for access for oneself. Nothing here changes until somebody approves —
 * but one's own requests list does, so it is refreshed.
 */
export function useRequestAccess() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: NewAccessRequest) => requestAccess(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accessKeys.requests() }),
  })
}

/** What the signed-in person can reach in this organisation, per application and place. */
export function useMyAccess() {
  return useQuery({ queryKey: accessKeys.mine(), queryFn: myAccess })
}

/** One's own access requests, newest first. */
export function useMyRequests() {
  return useQuery({ queryKey: accessKeys.requests(), queryFn: myRequests })
}

/** Withdraw one's own pending request; the list is refreshed either way. */
export function useCancelRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => cancelRequest(id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: accessKeys.requests() }),
  })
}

/** Places of one kind whose name contains `search`, once two characters are typed. */
export function useRequestablePlaces(kind: AccessKind, search: string) {
  const typed = search.trim()
  return useQuery({
    queryKey: accessKeys.places(kind, typed),
    queryFn: () => requestablePlaces(kind, typed),
    enabled: typed.length >= MIN_PLACE_SEARCH,
    staleTime: 60_000,
  })
}

/** The roles that can be asked for on one place, once a place is chosen. */
export function useRoleOffers(kind: AccessKind, scopeId: number | undefined) {
  return useQuery({
    queryKey: accessKeys.offers(kind, scopeId ?? -1),
    queryFn: () => roleOffers(kind, scopeId ?? -1),
    enabled: scopeId !== undefined,
    staleTime: 5 * 60_000,
  })
}
